import {
  CreateUserRequest,
  MyUser,
  UpdateMyUserRequest,
  UpdateUserRequest,
  User,
  UsersPageQuery,
  UserWithRole,
} from "@/types/breeze/User";
import { PaginatedResult } from "@/types/breeze/Any";
import { BaseService } from "../BaseService";
import { Permission } from "@/types/breeze/RoleAndPermission";

export class UserService extends BaseService {
  public async getMyUser(): Promise<MyUser> {
    return this.getRequest(`/users/me`);
  }

  public async checkMyUserHasPermission(permission: string): Promise<boolean> {
    try {
      const response = await this.getRequest(
        `/users/me/has-permission?permissionName=${permission}`,
      );
      return response === true;
    } catch {
      return false;
    }
  }

  public async checkMyUserHasRole(roleName: string): Promise<boolean> {
    try {
      const response = await this.getRequest(
        `/users/me/has-role?roleName=${roleName}`,
      );
      return response === true;
    } catch {
      return false;
    }
  }

  public async checkMyUserIsAdmin(): Promise<boolean> {
    try {
      const response = await this.getRequest(`/users/me/is-admin`);
      return response === true;
    } catch {
      return false;
    }
  }

  public async getMyPermissions(): Promise<Permission[]> {
    return this.getRequest(`/users/me/permissions`);
  }

  public async getUser(userId: number): Promise<UserWithRole> {
    return this.getRequest(`/users/${userId}`);
  }

  public async getUserCredentials(userId: number): Promise<{
    connections: Array<{ type: string; provider: string; display_name: string; icon: string; connected_at: string }>;
    secrets: Array<{ type: string; field_name: string; field_label: string; entity_name: string; configured: boolean }>;
  }> {
    return this.getRequest(`/users/${userId}/credentials`);
  }

  public async getAllUsers(): Promise<UserWithRole[]> {
    return this.getRequest(`/users`);
  }

  public async getUsersPage(
    query: UsersPageQuery,
  ): Promise<PaginatedResult<UserWithRole>> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== "") params.set(key, String(value));
    }
    return this.getRequest(`/users?${params}`);
  }

  public async createUser(request: CreateUserRequest): Promise<User> {
    return this.postRequest(`/users`, request, "User created");
  }

  public async updateUser(
    userId: number,
    request: UpdateUserRequest,
  ): Promise<User> {
    return this.putRequest(`/users/${userId}`, request, "User updated");
  }

  public async updateMyUser(request: UpdateMyUserRequest): Promise<void> {
    return this.putRequest(
      `/users/me`,
      request,
      "Your details have been updated",
      false,
    );
  }

  public async deleteUser(userId: number): Promise<void> {
    return this.deleteRequest(`/users/${userId}`, "User deleted");
  }

  public async sendConfirmationEmail(userId: number): Promise<void> {
    return this.postRequest(
      `/users/${userId}/send-confirmation-email`,
      {},
      "Confirmation email sent",
    );
  }

  public async sendInvitationEmail(userId: number): Promise<void> {
    return this.postRequest(
      `/users/${userId}/send-invitation-email`,
      {},
      "Invitation email sent",
    );
  }

  public async markAsConfirmed(userId: number): Promise<void> {
    return this.postRequest(
      `/users/${userId}/mark-as-confirmed`,
      {},
      "User marked as confirmed",
    );
  }
}

export const userService = new UserService();
